import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import { companyInfo, contactSections } from './contactUsContent';

import css from './ContactUsPage.module.css';

import { ReactComponent as DownloadIcon } from '../../assets/svg/downloadIcon.svg';

const Paragraph = ({ id }) => (
  <p className={css.paragraph}>
    <FormattedMessage id={id} />
  </p>
);

const Note = ({ id }) => (
  <p className={css.note}>
    <FormattedMessage id={id} />
  </p>
);

const Office = () => (
  <>
    <p className={css.paragraph}>{companyInfo.name}</p>
    <p className={css.paragraph}>{companyInfo.address}</p>
  </>
);

const Box = ({ titleId, bodyId, email }) => (
  <div className={css.box}>
    <p className={css.boxTitle}>
      <FormattedMessage id={titleId} />
    </p>
    {bodyId ? <Paragraph id={bodyId} /> : null}
    {email ? (
      <a className={css.emailLink} href={`mailto:${email}`}>
        {email}
      </a>
    ) : null}
  </div>
);

const Block = ({ block }) => {
  switch (block.type) {
    case 'p':
      return <Paragraph id={block.id} />;
    case 'note':
      return <Note id={block.id} />;
    case 'box':
      return <Box titleId={block.titleId} bodyId={block.bodyId} email={block.email} />;
    case 'office':
      return <Office />;
    default:
      return null;
  }
};

const Section = ({ section }) => {
  const boxBlocks = section.blocks.filter(block => block.type === 'box');
  const otherBlocks = section.blocks.filter(block => block.type !== 'box');

  const content = (
    <>
      <h2 className={css.sectionHeading}>
        <FormattedMessage id={section.titleId} />
      </h2>
      {boxBlocks.length > 0 ? (
        <div className={css.boxGrid}>
          {boxBlocks.map((block, index) => (
            <Block block={block} key={index} />
          ))}
        </div>
      ) : null}
      {otherBlocks.map((block, index) => (
        <Block block={block} key={index} />
      ))}
    </>
  );

  return (
    <div className={css.section}>
      {section.gradient ? (
        <div className={css.gradientBlock}>{content}</div>
      ) : section.accentBar ? (
        <div className={css.accentBarBlock}>{content}</div>
      ) : (
        content
      )}
    </div>
  );
};

/**
 * Content-only rendering of the Contact Us page, meant for embedding
 * elsewhere on the site.
 */
export const ContactUsContent = () => (
  <div className={css.card}>
    {contactSections.map((section, index) => (
      <Section section={section} key={index} />
    ))}
  </div>
);

export const ContactUsPageComponent = props => {
  const { scrollingDisabled } = props;
  const history = useHistory();
  const intl = useIntl();

  const handleDownloadPdf = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  const pageTitle = intl.formatMessage({ id: 'ContactUsPage.pageTitle' });

  return (
    <Page title={pageTitle} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.heroSection}>
            <div className={css.topRow}>
              <button className={css.backLink} onClick={() => history.goBack()} type="button">
                <FormattedMessage id="ContactUsPage.backBtn" />
              </button>
              <button className={css.downloadButton} onClick={handleDownloadPdf} type="button">
                <DownloadIcon className={css.icon} />
                <FormattedMessage id="ContactUsPage.downloadBtn" />
              </button>
            </div>

            <h1 className={css.title}>{pageTitle}</h1>
            <p className={css.subtitleRow}>
              <FormattedMessage id="ContactUsPage.intro" />
            </p>
          </div>

          <div className={css.content}>
            <ContactUsContent />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const ContactUsPage = connect(mapStateToProps)(ContactUsPageComponent);

export default ContactUsPage;
